import { mergeLineItemsIntoGroups } from 'modules/LineItems'
import partition from 'lodash/partition'

type LineItemGroup = {
  title: string
  id: string
}

export type LineItem = {
  id?: string
  /**
   * Source invoice line item ids for a multi-line roll-up. Only populated when
   * more than one line item is rolled into a single credit note line, so the
   * backend can inherit the source lines' accounting codes. 1:1 roll-ups use
   * `id` (sent as lineItemId) instead.
   */
  invoiceLineItemIds?: string[]
  taxRate: string
  groupId: string | null
  netTotal: string
  quantity: string
  title: string
  rate: string
  productId?: string
  servicePeriod?: {
    startDate: string
    endDate: string
  }
}

export function rollUpGroupsIntoLineItems<
  G extends LineItemGroup,
  L extends LineItem,
  A extends G & { lineItems: L[] }
>(lineItemGroups: G[], lineItems: L[], newGroup: LineItemGroup): A[] {
  const [orphanedLineItems, groupedLineItems] = partition(
    lineItems,
    lineItem => lineItem.groupId === null
  )

  const aggregatedGroups = mergeLineItemsIntoGroups<
    G,
    L & { groupId: string },
    A
  >(groupedLineItems as (L & { groupId: string })[], lineItemGroups)

  const [groupsToRollUp, remainingGroups] = partition(aggregatedGroups, group =>
    canBeRolledUp(group.lineItems)
  )

  if (!groupsToRollUp.length) {
    return remainingGroups
  }

  const rolledUp = groupsToRollUp.map(group => rollUpGroup(group, newGroup.id))

  const newGroupLineItems = [
    ...rolledUp,
    ...orphanedLineItems.map(item => ({
      ...item,
      groupId: newGroup.id
    }))
  ]

  return [
    { ...newGroup, lineItems: newGroupLineItems } as A,
    ...remainingGroups
  ]
}

function canBeRolledUp(lineItems: LineItem[]): boolean {
  const taxRates = new Set(lineItems.map(li => li.taxRate))
  const productIds = new Set(lineItems.map(li => li.productId))
  return taxRates.size === 1 && productIds.size === 1
}

function rollUpGroup(
  lineItemGroup: LineItemGroup & { lineItems: LineItem[] },
  parentGroupId: string
): LineItem {
  const netTotal = lineItemGroup.lineItems.reduce((acc, lineItem) => {
    return acc + Number(lineItem.netTotal)
  }, 0)

  const isSingleItem = lineItemGroup.lineItems.length === 1

  return {
    // 1:1 roll-ups keep the single source line's id (sent as lineItemId).
    // Multi-line roll-ups instead reference every source line via
    // invoiceLineItemIds so the backend can inherit their accounting codes.
    id: isSingleItem ? lineItemGroup.lineItems[0].id : undefined,
    invoiceLineItemIds: isSingleItem
      ? undefined
      : lineItemGroup.lineItems
          .map(({ id }) => id)
          .filter((id): id is string => id !== undefined),
    taxRate: lineItemGroup.lineItems[0].taxRate,
    groupId: parentGroupId,
    netTotal: netTotal.toString(),
    rate: netTotal.toString(),
    quantity: '1',
    title: lineItemGroup.title,
    // canBeRolledUp guarantees all line items share the same productId
    productId: lineItemGroup.lineItems[0].productId,
    // Derived for multi-item roll-ups so the credit note line carries a
    // concrete service period. Kept even though we now link the source lines
    // via invoiceLineItemIds, so revenue recognition does not depend on the
    // backend deriving the period from those references.
    servicePeriod:
      lineItemGroup.lineItems.length > 1
        ? deriveServicePeriod(lineItemGroup.lineItems)
        : undefined
  }
}

function deriveServicePeriod(lineItems: LineItem[]): LineItem['servicePeriod'] {
  const periods = lineItems
    .map(li => li.servicePeriod)
    .filter((p): p is NonNullable<typeof p> => p !== undefined)

  if (!periods.length) {
    return undefined
  }

  return {
    startDate: periods.reduce(
      (min, p) => (p.startDate < min ? p.startDate : min),
      periods[0].startDate
    ),
    endDate: periods.reduce(
      (max, p) => (p.endDate > max ? p.endDate : max),
      periods[0].endDate
    )
  }
}
